import { useCallback, useEffect, useState, useRef } from "react";
import { IFRAME_ID } from "../constants/privacyPolicyConstants";
import { applyIframeTheme, setIframeHeight } from "../utils/privacyPolicyUtils";

interface UsePrivacyPolicyIframeReturn {
  shouldRenderIframes: boolean;
  iframesReady: boolean;
  iframeGeneration: number;
  handleIframeLoad: (iframeId: string, generation: number) => void;
  triggerIframeReflow: () => void;
  resetIframeLoadStates: () => void;
}

/**
 * Custom hook for managing privacy policy iframe
 * Handles iframe rendering, loading, dark mode application and resize handling
 */
export const usePrivacyPolicyIframe = (
  isDarkMode: boolean,
  hasLPP?: boolean
): UsePrivacyPolicyIframeReturn => {
  const [shouldRenderIframes, setShouldRenderIframes] = useState(false);
  const [globalIframeReady, setGlobalIframeReady] = useState(false);
  const [localIframeReady, setLocalIframeReady] = useState(false);
  const [iframeGeneration, setIframeGeneration] = useState(0);
  const prevHasLPPRef = useRef(hasLPP);

  const iframesReady = globalIframeReady && (!hasLPP || localIframeReady);

  // Reset load states when LPP availability changes
  useEffect(() => {
    if (prevHasLPPRef.current !== hasLPP) {
      prevHasLPPRef.current = hasLPP;
      setGlobalIframeReady(false);
      setLocalIframeReady(false);
      setIframeGeneration((g) => g + 1);
    }
  }, [hasLPP]);

  /**
   * Handle iframe load event
   * Applies dark mode theme and sets iframe height
   * Tracks generation to prevent stale callbacks
   */
  const handleIframeLoad = useCallback(
    (iframeId: string, generation: number) => {
      // Ignore stale callbacks from previous generations
      if (generation !== iframeGeneration) {
        return;
      }

      const iFrame = document.getElementById(
        iframeId
      ) as HTMLIFrameElement | null;

      if (!iFrame?.contentWindow || !iFrame.contentDocument) {
        return;
      }

      const doc = iFrame.contentDocument;

      // Apply dark mode theme to iframe
      applyIframeTheme(doc, isDarkMode);

      // Set iframe height based on content
      setIframeHeight(doc, iFrame);

      // Mark iframe as loaded
      if (iframeId === IFRAME_ID) {
        setGlobalIframeReady(true);
      } else if (iframeId === `${IFRAME_ID}-local`) {
        setLocalIframeReady(true);
      }
    },
    [isDarkMode, iframeGeneration]
  );

  /**
   * Trigger iframe rendering for initial load
   * Used to handle delayed layout calculation on page refresh
   */
  const triggerIframeReflow = useCallback(() => {
    setShouldRenderIframes(true);
  }, []);

  // Set up resize listener for iframe height adjustment
  useEffect(() => {
    if (!globalIframeReady) return;

    const handleResize = () => {
      handleIframeLoad(IFRAME_ID, iframeGeneration);
      if (hasLPP) {
        handleIframeLoad(`${IFRAME_ID}-local`, iframeGeneration);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [globalIframeReady, hasLPP, handleIframeLoad, iframeGeneration]);

  const resetIframeLoadStates = useCallback(() => {
    setGlobalIframeReady(false);
    setLocalIframeReady(false);
    setIframeGeneration((g) => g + 1);
  }, []);

  return {
    shouldRenderIframes,
    iframesReady,
    iframeGeneration,
    handleIframeLoad,
    triggerIframeReflow,
    resetIframeLoadStates,
  };
};
